/**
 * Route Path Constants
 *
 * 모든 라우트 경로를 상수로 관리하여 타입 안정성과 유지보수성 향상
 */
export const ROUTES = {
  // Public Routes
  HOME: "/",

  // Authentication Routes
  AUTH: {
    LOGIN: "/login",
    SIGNUP: "/signup",
    SIGNUP_COMPLETE: "/signup/complete",
    EMAIL_VERIFICATION: "/verify-email",
    EMAIL_VERIFICATION_COMPLETE: "/verify-email/complete",
    PASSWORD_RESET: "/password-reset",
    PASSWORD_RESET_NEW: "/password-reset/new",
    EMAIL_CHANGE: "/email-change",
    EMAIL_CHANGE_COMPLETE: "/email-change/complete",
    ACCOUNT_DELETION_REQUEST: "/account-deletion/request",
    ACCOUNT_DELETION_CONFIRM: "/account-deletion/confirm",
    ACCOUNT_DELETION_COMPLETE: "/account-deletion/complete",
    SETTINGS: "/settings",
    ADD_ACCOUNT: "/settings/add-account",
    /** 대한병원협회 회원 인증 페이지 */
    KHA_VERIFY: "/auth/kha-verify",
    /** SSO 로그인 */
    SSO_LOGIN: (origin: string, userSsoAccessId: string) => `/sso/${origin}/login/${userSsoAccessId}`,
    /** SSO 회원가입 */
    SSO_SIGNUP: (origin: string, userSsoAccessId: string) => `/sso/${origin}/signup/${userSsoAccessId}`,
    /** SSO 계정 연동 */
    SSO_LINK: (origin: string, userSsoAccessId: string) => `/sso/${origin}/link/${userSsoAccessId}`,
    /** SSO placeholder (signup/link 다음 단계) */
    SSO_PLACEHOLDER: (origin: string, type: string, userSsoAccessId: string) => `/sso/${origin}/${type}/${userSsoAccessId}/form`,
  },

  // Student Routes
  STUDENT: {
    BASE: "/student",
  },

  // Master Routes
  MASTER: {
    BASE: "/master",
    MY_CLASSES: "/master/my-classes",
    CLASS_MANAGEMENT: "/master/class-management",
    CLASS_DETAIL: (id: string) => `/master/my-classes/${id}`,
    CLASS_CREATE: "/master/class/create",
    CLASS_EDIT: (id: string) => `/master/class/edit/${id}`,
    CLASS_CURRICULUM: (classId: string) =>
      `/master/class-management/${classId}/curriculum`,
    CLASS_SESSION: (classId: string, sessionId: string) =>
      `/master/class-management/${classId}/curriculum/${sessionId}`,
    CLASS_RESULTS: (classId: string, sessionId: string) =>
      `/master/class-management/${classId}/results/${sessionId}`,
    /** 사용자별 평가결과 (클래스·세션·사용자 ID 지정) */
    CLASS_USER_RESULTS: (classId: string, sessionId: string, userId: string) =>
      `/master/class-management/${classId}/results/${sessionId}/user/${userId}`,
    PRODUCT_MANAGEMENT: "/master/product-management",
    OPEN_CLASS_CREATE: "/master/open-class/create",
    SCENARIOS: "/master/scenarios",
    SCENARIO_DETAIL: (id: string) => `/master/scenarios/${id}`,
    ASSETS_EVENTS: "/master/assets/events",
    ASSETS_EVENT_CREATE: "/master/assets/events/create",
    ASSETS_EVENT_EDIT: (id: string) => `/master/assets/events/${id}/edit`,
    ASSETS_SYMPTOMS: "/master/assets/symptoms",
    ASSETS_TASKS: "/master/assets/tasks",
    ASSETS_ACTIONS: "/master/assets/actions",
    ASSETS_DIALOGUES: "/master/assets/dialogues",
    ORGANIZATION: "/master/organization",
    /** 관리자 페이지 > 클래스 관리 탭 (저장/취소 후 이동) */
    ORGANIZATION_CLASS_MANAGEMENT:
      "/master/organization?section=class-management",
    /** 마스터 기관 주문 목록 (스프레드시트: 관리자 > 주문내역) */
    ORDER_MANAGEMENT: "/master/order-management",
    ORDERS: "/master/orders",
  },

  // Admin Routes
  ADMIN: {
    BASE: "/admin",
    /** 어드민 "관리자" 메뉴용 단일 기관 페이지 (마스터 관리자 페이지와 동일 UI) */
    ORGANIZATION: "/admin/organization",
    /** 관리자 페이지 > 클래스 관리 탭 (클래스 생성 완료/취소 후 이동) */
    ORGANIZATION_CLASS_MANAGEMENT:
      "/admin/organization?section=class-management",
    ORGANIZATIONS: "/admin/organizations",
    ORGANIZATION_DETAIL: (id: string) => `/admin/organizations/${id}`,
    USERS: "/admin/users",
    SCENARIOS: "/admin/scenarios",
    SCENARIO_ADD: "/admin/scenarios/add",
    SCENARIO_EDIT: (id: string | number) => `/admin/scenarios/${id}/edit`,
    SCENARIO_DETAIL: (id: string) => `/admin/scenarios/${id}`,
    CLASS_MANAGEMENT: "/admin/class-management",
    CLASS_DETAIL: (id: string) => `/admin/my-classes/${id}`,
    CLASS_CREATE: "/admin/class/create",
    CLASS_EDIT: (id: string) => `/admin/class/edit/${id}`,
    CLASS_CURRICULUM: (classId: string) =>
      `/admin/class-management/${classId}/curriculum`,
    CLASS_SESSION: (classId: string, sessionId: string) =>
      `/admin/class-management/${classId}/curriculum/${sessionId}`,
    CLASS_RESULTS: (classId: string, sessionId: string) =>
      `/admin/class-management/${classId}/results/${sessionId}`,
    /** 사용자별 평가결과 */
    CLASS_USER_RESULTS: (classId: string, sessionId: string, userId: string) =>
      `/admin/class-management/${classId}/results/${sessionId}/user/${userId}`,
    PRODUCT_MANAGEMENT: "/admin/product-management",
    ORDER_MANAGEMENT: "/admin/order-management",
    OPEN_CLASS_CREATE: "/admin/open-class/create",
    OPEN_CLASS_DETAIL: (id: string) => `/admin/open-class/${id}`,
    OPEN_CLASS_EDIT: (id: string) => `/admin/open-class/edit/${id}`,
    ASSETS_EVENTS: "/admin/assets/events",
    ASSETS_EVENT_CREATE: "/admin/assets/events/create",
    ASSETS_EVENT_EDIT: (id: string) => `/admin/assets/events/${id}/edit`,
    ASSETS_SYMPTOMS: "/admin/assets/symptoms",
    ASSETS_TASKS: "/admin/assets/tasks",
    ASSETS_ACTIONS: "/admin/assets/actions",
    ASSETS_DIALOGUES: "/admin/assets/dialogues",
    ASSETS_ITEMS: "/admin/assets/items",
    /** 관리자 자료실 목록 */
    RESOURCES: "/admin/resources",
    /** 관리자 자료 등록 */
    RESOURCES_CREATE: "/admin/resources/create",
    /** 관리자 자료 상세 (레이아웃 유지) */
    RESOURCE_DETAIL: (id: string) => `/admin/resources/${id}`,
    /** 관리자 자료 수정 */
    RESOURCES_EDIT: (id: string) => `/admin/resources/${id}/edit`,
    /** 관리자 블로그 목록 */
    BLOG: "/admin/blog",
    /** 관리자 블로그 게시글 등록 */
    BLOG_CREATE: "/admin/blog/create",
    /** 관리자 블로그 상세 (레이아웃 유지) */
    BLOG_POST: (id: string) => `/admin/blog/${id}`,
    /** 관리자 블로그 게시글 수정 */
    BLOG_EDIT: (id: string) => `/admin/blog/${id}/edit`,
  },

  // Common Routes
  COMMON: {
    CLASS_LIST: "/open-class-list",
    BLOG: "/blog",
    BLOG_POST: (id: string) => `/blog/${id}`,
    RESOURCES: "/resources",
    RESOURCE_DETAIL: (id: string) => `/resources/${id}`,
    OPEN_CLASS_DETAIL: (id: string) => `/open-class/${id}`,
    CLASS_DETAIL: "/class-detail",
    CLASS_INVITE_ACCEPT: (token: string) => `/class/invite/${token}`,
    CLASS_CURRICULUM: (classId: string) => `/class/${classId}/curriculum`,
    CLASS_SESSION: (classId: string, sessionId: string) =>
      `/class/${classId}/curriculum/${sessionId}`,
    CLASS_RESULTS: (classId: string, sessionId: string) =>
      `/class/${classId}/results/${sessionId}`,
    /** 사용자별 평가결과 */
    CLASS_USER_RESULTS: (classId: string, sessionId: string, userId: string) =>
      `/class/${classId}/results/${sessionId}/user/${userId}`,
    SCENARIOS: "/scenarios",
    SCENARIO_DETAIL: (id: string) => `/scenario/${id}`,
    /** 이용약관 */
    TERMS: "/terms",
    /** 개인정보처리방침 */
    PRIVACY: "/privacy-policy",
    /** 고객문의 */
    CONTACT: "/contact",
    /** 장바구니 (로그인 전에도 이용 가능) */
    CART: "/cart",
    /** 주문정보 확인 (장바구니 → 온라인 주문 시 이동) */
    ORDER_CONFIRM: "/order-confirm",
    /** 토스페이먼츠 결제 리다이렉트 (일반결제) */
    /** 토스 리다이렉트 전용 — `/payment/*` 는 dev 프록시·게이트웨이와 겹치기 쉬워 `/pay/...` 로 둔다 */
    PAYMENT_TOSS_SUCCESS: "/pay/toss/success",
    PAYMENT_TOSS_FAIL: "/pay/toss/fail",
  },

  // Studio Routes
  STUDIO: {
    EDIT: "/studio/edit",
  },

  // Development Routes
  DEV: {
    EMAIL_PREVIEW: "/dev/email-preview",
  },

  // Error Pages
  ERROR: {
    NOT_FOUND: "/404",
    FORBIDDEN: "/403",
  },
} as const;

/**
 * Helper function to generate route paths with parameters
 */
export const buildRoute = {
  master: {
    classDetail: (id: string) => ROUTES.MASTER.CLASS_DETAIL(id),
    classEdit: (id: string) => ROUTES.MASTER.CLASS_EDIT(id),
    classCurriculum: (classId: string) =>
      ROUTES.MASTER.CLASS_CURRICULUM(classId),
    classSession: (classId: string, sessionId: string) =>
      ROUTES.MASTER.CLASS_SESSION(classId, sessionId),
    classResults: (classId: string, sessionId: string) =>
      ROUTES.MASTER.CLASS_RESULTS(classId, sessionId),
    classUserResults: (classId: string, sessionId: string, userId: string) =>
      ROUTES.MASTER.CLASS_USER_RESULTS(classId, sessionId, userId),
    scenarioDetail: (id: string) => ROUTES.MASTER.SCENARIO_DETAIL(id),
    eventEdit: (id: string) => ROUTES.MASTER.ASSETS_EVENT_EDIT(id),
  },
  admin: {
    organizationDetail: (id: string) => ROUTES.ADMIN.ORGANIZATION_DETAIL(id),
    classDetail: (id: string) => ROUTES.ADMIN.CLASS_DETAIL(id),
    classEdit: (id: string) => ROUTES.ADMIN.CLASS_EDIT(id),
    classCurriculum: (classId: string) =>
      ROUTES.ADMIN.CLASS_CURRICULUM(classId),
    classSession: (classId: string, sessionId: string) =>
      ROUTES.ADMIN.CLASS_SESSION(classId, sessionId),
    classResults: (classId: string, sessionId: string) =>
      ROUTES.ADMIN.CLASS_RESULTS(classId, sessionId),
    classUserResults: (classId: string, sessionId: string, userId: string) =>
      ROUTES.ADMIN.CLASS_USER_RESULTS(classId, sessionId, userId),
    scenarioDetail: (id: string) => ROUTES.ADMIN.SCENARIO_DETAIL(id),
    eventEdit: (id: string) => ROUTES.ADMIN.ASSETS_EVENT_EDIT(id),
  },
  common: {
    classCurriculum: (classId: string) =>
      ROUTES.COMMON.CLASS_CURRICULUM(classId),
    classSession: (classId: string, sessionId: string) =>
      ROUTES.COMMON.CLASS_SESSION(classId, sessionId),
    classResults: (classId: string, sessionId: string) =>
      ROUTES.COMMON.CLASS_RESULTS(classId, sessionId),
    classUserResults: (classId: string, sessionId: string, userId: string) =>
      ROUTES.COMMON.CLASS_USER_RESULTS(classId, sessionId, userId),
    scenarioDetail: (id: string) => ROUTES.COMMON.SCENARIO_DETAIL(id),
  },
};
